import { useSearchParams } from 'react-router-dom';
import { KeyRound, Activity, Phone, Mail, MessageSquare, MessageCircle, Warehouse, ListChecks, AlertTriangle, Landmark, Receipt, Webhook, Package, Building2, MapPinned, Clock, Megaphone, ScrollText, CalendarClock } from 'lucide-react';
import { ChangePasswordPanel } from './ChangePasswordPanel';
import { SmtpSettingsPanel } from './SmtpSettingsPanel';
import { SmsSettingsPanel } from './SmsSettingsPanel';
import { WhatsappSettingsPanel } from './WhatsappSettingsPanel';
import { MasterWarehousesPanel } from './MasterWarehousesPanel';
import { OrderStatusesPanel } from './OrderStatusesPanel';
import { AiChatbotPanel } from './AiChatbotPanel';
import { AiChatLogPanel } from './AiChatLogPanel';
import { NdrStatusesPanel } from './NdrStatusesPanel';
import { IvrRemarksPanel } from './IvrRemarksPanel';
import { BankRechargePanel } from './BankRechargePanel';
import { InvoiceCompaniesPanel } from './InvoiceCompaniesPanel';
import { MerchantWebhooksPanel } from './MerchantWebhooksPanel';
import { OwnCourierSeriesPanel } from './OwnCourierSeriesPanel';
import { SuperAdminProfilePanel } from './SuperAdminProfilePanel';
import { TatZonesPanel } from './TatZonesPanel';
import { IvrSettingsPanel } from './IvrSettingsPanel';
import { ActivityLogPanel } from './ActivityLogPanel';
import { ErrorLogPanel } from './ErrorLogPanel';
import { NotificationTemplatesTab } from './NotificationTemplatesTab';
import { CronHistoryPanel } from './CronHistoryPanel';
import { MisSchedulePanel } from './MisSchedulePanel';
import { MerchantNoticesPanel } from './MerchantNoticesPanel';
import { AppointmentCustomersPanel } from './AppointmentCustomersPanel';
import { CourierPasswordsPanel } from './CourierPasswordsPanel';
import { useAuth } from '@/hooks/useAuth';

const SECTIONS = [
  {
    label: 'Platform',
    tabs: [
      { id: 'super-admin-profile', label: 'Super Admin Profile', Icon: Building2, desc: 'Company name, brand & logo' },
      { id: 'activity-log', label: 'Activity Log', Icon: Activity, Phone, desc: 'Every action by users + merchants' },
      { id: 'error-log', label: 'Error Log', Icon: AlertTriangle, desc: 'Server errors captured for review' },
      { id: 'courier-passwords', label: 'Courier Passwords', Icon: KeyRound, desc: 'Delhivery B2B password expiry — repairs itself, watch it here', adminOnly: true },
      { id: 'cron-history', label: 'Cron History', Icon: Clock, desc: 'Every scheduled job, last run & records affected', adminOnly: true },
      { id: 'mis-schedule', label: 'MIS Schedule', Icon: Clock, desc: 'Daily master-data sheet emailed to merchants', adminOnly: true },
      { id: 'ai-chatbot', label: 'AI Chatbot', Icon: KeyRound, desc: 'Gemini / DeepSeek API + credit caps' },
      { id: 'ai-chat-log', label: 'AI Chatbot Log', Icon: ScrollText, desc: 'Every question asked, the SQL it ran and what it answered', adminOnly: true },
      { id: 'merchant-notices', label: 'Merchant Notifications', Icon: Megaphone, desc: 'Marquee shown to merchants — outages, downtime', adminOnly: true },
    ],
  },
  {
    label: 'Account',
    tabs: [
      { id: 'password', label: 'Change Password', Icon: KeyRound, desc: 'Update your account password' },
    ],
  },
  {
    label: 'Gateways',
    tabs: [
      { id: 'smtp', label: 'SMTP (Email)', Icon: Mail, desc: 'Outbound email gateway' },
      { id: 'sms', label: 'SMS Gateway', Icon: MessageSquare, desc: 'DLT-compliant transactional SMS' },
      { id: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle, desc: 'Meta templates, events, price, history, invoices' },
      { id: 'sms-templates', label: 'SMS Templates', Icon: MessageSquare, desc: 'Link templates to triggers (OTP, booked, delivered…)' },
      { id: 'email-templates', label: 'Email Templates', Icon: Mail, desc: 'Subject + body templates per trigger' },
      { id: 'ivr', label: 'IVR Click-to-Call', Icon: Phone, desc: 'prpmobility settings + call log + webhook' },
    ],
  },
  {
    label: 'Master Data',
    tabs: [
      { id: 'master-warehouses', label: 'Merchant Pickup Locations', Icon: Warehouse, desc: 'Shared pickup locations for merchants' },
      { id: 'order-statuses', label: 'Order Statuses', Icon: ListChecks, desc: 'Tracking status master' },
      { id: 'ndr-statuses', label: 'NDR Reasons', Icon: AlertTriangle, desc: 'Delivery failure reason codes' },
      { id: 'ivr-remarks', label: 'IVR Call Remarks', Icon: Phone, desc: 'Customer Remarks dropdown on the IVR screen' },
      { id: 'merchant-webhooks', label: 'Merchant Webhooks', Icon: Webhook, desc: 'Outbound tracking push URLs' },
      { id: 'own-courier-series', label: 'Own Courier Series', Icon: Package, desc: 'Gaint AWB / LR auto-numbering' },
      { id: 'tat-zones', label: 'TAT Zones', Icon: MapPinned, desc: 'Zone-wise TAT days (B2C + B2B)' },
      { id: 'appointment-customers', label: 'Appointments Customer', Icon: CalendarClock, desc: 'B2B customers that get an appointment raised at booking', adminOnly: true },
    ],
  },
  {
    label: 'Billing',
    tabs: [
      { id: 'invoice-companies', label: 'GST Details', Icon: Receipt, desc: 'Per-GSTIN invoice profile' },
      { id: 'bank-recharge', label: 'Bank for Recharge', Icon: Landmark, desc: 'Wallet recharge bank account' },
    ],
  },
] as const;

type TabId =
  | 'super-admin-profile' | 'activity-log' | 'error-log' | 'courier-passwords'
  | 'password' | 'smtp' | 'sms' | 'whatsapp' | 'sms-templates' | 'email-templates' | 'ivr'
  | 'master-warehouses' | 'order-statuses' | 'ndr-statuses' | 'ivr-remarks'
  | 'merchant-webhooks' | 'own-courier-series' | 'tat-zones'
  | 'invoice-companies' | 'bank-recharge' | 'ai-chatbot' | 'ai-chat-log' | 'cron-history' | 'merchant-notices' | 'mis-schedule'
  | 'appointment-customers';

export function SettingsPage() {
  const { user } = useAuth();
  // Cron history exposes platform-wide job volumes across every merchant — Super Admin / Admin only,
  // matching the backend @Roles on /cron-monitor so the tab can't offer something the API refuses.
  const isAdmin = user?.userType === 'SUPER_ADMIN' || user?.userType === 'ADMIN';

  // The chosen panel lives in the URL (`/settings?tab=cron-history`) so a specific settings screen
  // can be linked to and bookmarked directly, instead of only being reachable by hunting through an
  // 18-item side nav.
  const [params, setParams] = useSearchParams();
  const urlTab = params.get('tab') as TabId | null;
  const tab: TabId = urlTab ?? 'super-admin-profile';
  const setTab = (id: TabId) => setParams({ tab: id }, { replace: true });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 text-sm mt-1">Account, gateways, master data and billing configuration</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden grid grid-cols-[280px_1fr] min-h-[520px]">
        {/* Left side nav */}
        <nav className="border-r border-slate-200 bg-slate-50 p-3 space-y-4 overflow-y-auto">
          {SECTIONS.map((section) => (
            <div key={section.label}>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 mb-1">
                {section.label}
              </div>
              <div className="space-y-1">
                {section.tabs.filter((t) => !(t as any).adminOnly || isAdmin).map(({ id, label, Icon, desc }) => {
                  const active = tab === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setTab(id as TabId)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition flex items-start gap-2.5 ${
                        active ? 'bg-white border border-slate-200 shadow-sm' : 'hover:bg-slate-100'
                      }`}>
                      <Icon size={15} className={`mt-0.5 ${active ? 'text-primary' : 'text-slate-500'}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${active ? 'text-primary' : 'text-slate-900'}`}>{label}</div>
                        <div className="text-xs text-slate-500 truncate">{desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Right side content */}
        <div className="p-6 overflow-y-auto">
          {tab === 'super-admin-profile' && <SuperAdminProfilePanel />}
          {tab === 'activity-log' && <ActivityLogPanel />}
          {tab === 'error-log' && <ErrorLogPanel />}
          {tab === 'courier-passwords' && <CourierPasswordsPanel />}
          {tab === 'mis-schedule' && <MisSchedulePanel />}
          {tab === 'cron-history' && <CronHistoryPanel />}
          {tab === 'merchant-notices' && <MerchantNoticesPanel />}
          {tab === 'password' && <ChangePasswordPanel />}
          {tab === 'smtp' && <SmtpSettingsPanel />}
          {tab === 'sms' && <SmsSettingsPanel />}
          {tab === 'whatsapp' && <WhatsappSettingsPanel />}
          {tab === 'sms-templates' && <NotificationTemplatesTab channel="sms" />}
          {tab === 'email-templates' && <NotificationTemplatesTab channel="email" />}
          {tab === 'ivr' && <IvrSettingsPanel />}
          {tab === 'master-warehouses' && <MasterWarehousesPanel />}
          {tab === 'order-statuses' && <OrderStatusesPanel />}
          {tab === 'ndr-statuses' && <NdrStatusesPanel />}
          {tab === 'ivr-remarks' && <IvrRemarksPanel />}
          {tab === 'merchant-webhooks' && <MerchantWebhooksPanel />}
          {tab === 'own-courier-series' && <OwnCourierSeriesPanel />}
          {tab === 'tat-zones' && <TatZonesPanel />}
          {tab === 'appointment-customers' && <AppointmentCustomersPanel />}
          {tab === 'ai-chatbot' && <AiChatbotPanel />}
          {tab === 'ai-chat-log' && <AiChatLogPanel />}
          {tab === 'invoice-companies' && <InvoiceCompaniesPanel />}
          {tab === 'bank-recharge' && <BankRechargePanel />}
        </div>
      </div>
    </div>
  );
}
